import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { 
    Zap, Shield, Users, Settings, Save, Search, Plus, 
    ToggleLeft, ToggleRight, Lock, Activity, AlertTriangle, 
    Server, X, FileCode, LayoutTemplate, MessageSquare, Mail, Trash2, Edit3, Check, Archive, Ban, Moon 
} from 'lucide-react';
import { PERMISSIONS_MATRIX, COMM_TEMPLATES, ROLES, SYSTEM_TRIGGERS } from '../data/constants';
import { ROLE_CONFIG } from '../data/RoleConfig';

// --- LOCAL MOCK DATA FOR ADMIN ---
const INITIAL_SYSTEM_USERS = [
    { id: 1, name: 'Dave Manager', email: 'dave@nommia.com', role: 'Manager', status: 'Active', lastLogin: 'Today, 09:00', twoFactor: true },
    { id: 2, name: 'Sarah Sales', email: 'sarah@nommia.com', role: 'Sales', status: 'Active', lastLogin: 'Today, 09:15', twoFactor: true },
    { id: 3, name: 'Mike Compliance', email: 'mike@nommia.com', role: 'Compliance', status: 'Active', lastLogin: 'Yesterday, 17:00', twoFactor: true },
    { id: 4, name: 'John Support', email: 'john@nommia.com', role: 'Support', status: 'Suspended', lastLogin: '2025-10-01', twoFactor: false },
];

const AdminPage = () => {
   const [activeTab, setActiveTab] = useState('templates'); 
   const [activeFolder, setActiveFolder] = useState('All'); // 🚨 NEW: Tracks the selected folder
   
   // --- STATE: USERS ---
   const [users, setUsers] = useState([]);
   const [loadingUsers, setLoadingUsers] = useState(true);
   const [isCreating, setIsCreating] = useState(false); // NEW: For the button loader
   const [userModal, setUserModal] = useState({ show: false, mode: 'add', data: null });
   const [userFormData, setUserFormData] = useState({ name: '', email: '', password: '', role: 'Sales_L1', status: 'Active' });

   // --- LIVE FETCH: Pull Staff from Supabase ---
   useEffect(() => {
       const fetchLiveStaff = async () => {
           try {
               const { data, error } = await supabase
                   .from('profiles')
                   .select('*')
                   .neq('role', 'Client')
                   .order('full_name');

               if (error) throw error;

               if (data) {
                   const mappedStaff = data
                       .filter(profile => profile.status !== 'deleted') // Hide deleted users
                       .map(profile => {
                           let displayStatus = 'Active';
                           if (profile.status === 'suspended') displayStatus = 'Suspended';
                           if (profile.status === 'archived') displayStatus = 'Archived';

                           return {
                               id: profile.id,
                               name: profile.full_name || 'Unnamed Agent',
                               email: profile.email || 'No Email Provided', 
                               role: profile.role?.replace('_', ' ') || 'Staff',
                               status: displayStatus,
                               lastLogin: 'Recently', 
                               twoFactor: true 
                           };
                       });
                   setUsers(mappedStaff);
               }
           } catch (err) {
               console.error("Failed to load live staff:", err);
           } finally {
               setLoadingUsers(false);
           }
       };

       if (activeTab === 'users') {
           fetchLiveStaff();
       }
   }, [activeTab]);

   // --- LIVE FETCH: Pull Templates from Supabase ---
   useEffect(() => {
       const fetchTemplates = async () => {
           if (activeTab !== 'templates') return;

           try {
               const { data, error } = await supabase.from('crm_templates').select('*');
               if (error) throw error;

               if (data) {
                   // 1. Grab the Master HTML
                   const master = data.find(t => t.id === 'master_html');
                   if (master && master.body) setMasterHtml(master.body);

                  // 2. Grab the Email Templates
                   const emails = data.filter(t => t.type === 'email').map(t => ({
                       id: t.id,
                       label: t.label,
                       subject: t.subject,
                       body: t.body,
                       useCustomHtml: t.use_custom_html, // <-- COMMA ADDED HERE
                       isSystem: t.is_system || false,
                       folder: t.folder || 'Uncategorized'
                   }));
                   // Always sync exactly with the live DB
                   setEmailTemplates(emails);

                   // 3. Grab the WhatsApp Templates
                   const whatsapps = data.filter(t => t.type === 'whatsapp').map(t => ({
                       id: t.id,
                       label: t.label,
                       body: t.body,
                       isSystem: t.is_system || false
                   }));
                   // Always sync exactly with the live DB
                   setWhatsappTemplates(whatsapps);
               }
           } catch (err) {
               console.error("Failed to load templates:", err);
           }
       };

       fetchTemplates();
   }, [activeTab]);

   // --- NEW HANDLER: Status Actions (Suspend, Archive, Delete) ---
   const handleUserAction = async (userId, actionType) => {
       let confirmMsg = "";
       let newStatus = "";

       if (actionType === 'delete') {
           confirmMsg = "Are you sure you want to DELETE this user? They will be removed from this list.";
           newStatus = "deleted";
       } else if (actionType === 'suspend') {
           confirmMsg = "Are you sure you want to SUSPEND this user's access?";
           newStatus = "suspended";
       } else if (actionType === 'archive') {
           confirmMsg = "Are you sure you want to ARCHIVE this user?";
           newStatus = "archived";
       } else if (actionType === 'restore') {
           confirmMsg = "Restore this user's access to Active?";
           newStatus = "offline"; // Resets them to standard presence tracking
       }

       if (!window.confirm(confirmMsg)) return;

       try {
           const { error } = await supabase
               .from('profiles')
               .update({ status: newStatus })
               .eq('id', userId);

           if (error) throw error;

           // Update UI instantly without reloading
           if (actionType === 'delete') {
               setUsers(users.filter(u => u.id !== userId));
           } else {
               const displayStatus = actionType === 'restore' ? 'Active' : actionType.charAt(0).toUpperCase() + actionType.slice(1);
               setUsers(users.map(u => u.id === userId ? { ...u, status: displayStatus } : u));
           }
       } catch (err) {
           console.error("Error updating user status:", err);
           alert(`Failed to ${actionType} user.`);
       }
   };

   // --- STATE: PERMISSIONS ---
   const [permissions, setPermissions] = useState(PERMISSIONS_MATRIX);
   const [showSchemaModal, setShowSchemaModal] = useState(false);

// --- STATE: AUTOMATION ---
   const [automationRules, setAutomationRules] = useState([]);
   const [showRuleModal, setShowRuleModal] = useState(false);
   const [newRule, setNewRule] = useState({ trigger: 'REGISTRATION', channel: 'email', templateId: '' });

   // --- LIVE FETCH: Pull Automation Rules ---
   useEffect(() => {
       const fetchRules = async () => {
           if (activeTab !== 'automation') return;
           try {
               const { data, error } = await supabase.from('crm_automation_rules').select('*').order('created_at', { ascending: false });
               if (error) throw error;
               if (data) {
                   setAutomationRules(data.map(r => ({
                       id: r.id,
                       trigger: r.trigger_event,
                       channel: r.channel,
                       templateId: r.template_id,
                       active: r.is_active
                   })));
               }
           } catch (err) {
               console.error("Failed to load automation rules:", err);
           }
       };
       fetchRules();
   }, [activeTab]);

// --- STATE: TEMPLATES ---
   // Flattening mock data for local editing state
   const [emailTemplates, setEmailTemplates] = useState([
       ...COMM_TEMPLATES.email, 
       { 
           id: 'ticket_autoclose', 
           label: 'Ticket Auto-Close Notice', 
           subject: 'Notice: Your Support Ticket #{{ticket_id}} has been closed', 
           body: 'Dear {{client_name}},\n\nWe noticed you haven\'t replied to ticket #{{ticket_id}} in over 14 days. As a result, our system has automatically closed this ticket.\n\nIf you still require assistance, please simply reply to this email or open a new ticket via your portal.\n\nBest regards,\nNommia Support Team' 
       }
   ]);
   const [whatsappTemplates, setWhatsappTemplates] = useState(COMM_TEMPLATES.whatsapp);
   
   // Template Modal State
   const [templateModal, setTemplateModal] = useState({ show: false, mode: 'add', type: 'email', data: { id: null, label: '', subject: '', body: '' } });
   const [showPreview, setShowPreview] = useState(false);
   const [masterHtml, setMasterHtml] = useState(
`<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; background-color: #f4f4f4; }
    .container { max-width: 600px; margin: 0 auto; background: #ffffff; }
    .header { background: #0f172a; padding: 20px; text-align: center; }
    .content { padding: 30px; color: #334155; }
    .footer { background: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #94a3b8; }
    .btn { background: #d97706; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <img src="https://i.ibb.co/yc7GWG8v/Nommia-Gold-and-White-Logo.png" alt="Nommia" height="40" />
    </div>
    <div class="content">
      {{CONTENT_BODY}}
    </div>
    <div class="footer">
      &copy; 2025 Nommia Markets. All rights reserved.<br/>
      Investing involves risk.
    </div>
  </div>
</body>
</html>`);

   // --- STATE: CONFIG & SECURITY ---
   const [sysConfig, setSysConfig] = useState({ maintenanceMode: false, newRegistrations: true, outOfHours: false });
   const [securityConfig, setSecurityConfig] = useState({ enforce2FA: false, ipWhitelist: false, sessionTimeout: 30 });
   const [isSavingConfig, setIsSavingConfig] = useState(false);

   // --- LIVE FETCH: Pull Global Settings & Permissions ---
   useEffect(() => {
       const fetchSettings = async () => {
           // THE FIX: Allow it to run on the 'users' tab so the schema loads!
           if (activeTab !== 'config' && activeTab !== 'security' && activeTab !== 'users') return;
           try {
               const { data, error } = await supabase.from('crm_settings').select('*').eq('id', 'global').single();
               if (error) throw error;
               
               if (data) {
                   setSysConfig({ 
                       maintenanceMode: data.maintenance_mode, 
                       newRegistrations: data.new_registrations,
                       outOfHours: data.is_out_of_hours || false 
                   });
                   setSecurityConfig({ enforce2FA: data.enforce_2fa, ipWhitelist: data.ip_whitelist, sessionTimeout: data.session_timeout });
                   
                   // THE FIX: Load live page permissions, fallback to hardcoded CRM pages if DB is empty
                   if (data.role_permissions && data.role_permissions.length > 0) {
                       setPermissions(data.role_permissions);
                   } else {
                       setPermissions([
                           { id: 'page_overview', label: 'Overview Dashboard', roles: ['Admin', 'Manager', 'Sales', 'Support', 'Compliance'] },
                           { id: 'page_clients', label: 'Clients Database', roles: ['Admin', 'Manager', 'Sales', 'Support', 'Compliance'] },
                           { id: 'page_ib_partners', label: 'IB Partners', roles: ['Admin', 'Manager', 'Sales'] },
                           { id: 'page_compliance', label: 'Compliance (KYC/AML)', roles: ['Admin', 'Compliance', 'Manager'] },
                           { id: 'page_team_hub', label: 'Team Hub', roles: ['Admin', 'Manager'] },
                           { id: 'page_marketing', label: 'Marketing', roles: ['Admin', 'Manager', 'Sales'] },
                           { id: 'page_admin', label: 'System Admin', roles: ['Admin'] }
                       ]);
                   }
               }
           } catch (err) {
               console.error("Failed to load settings:", err);
           }
       };
       fetchSettings();
   }, [activeTab]);

   // --- HANDLERS: USERS ---
   const handleOpenUserModal = (mode, user = null) => {
       setUserModal({ show: true, mode, data: user });
       if (user) {
           setUserFormData({ name: user.name, email: user.email, role: user.role, status: user.status });
       } else {
           setUserFormData({ name: '', email: '', role: 'Sales', status: 'Active' });
       }
   };

   const handleSaveUser = async () => {
       if (!userFormData.name) return alert("Name is required.");
       
       try {
           setIsCreating(true);
           const formatRole = userFormData.role.replace(' ', '_');
           
           if (userModal.mode === 'add') {
               if (!userFormData.email || !userFormData.password) return alert("Email and Password are required to create a new user.");
               
               // Ping your existing Edge Function!
               const { error } = await supabase.functions.invoke('create-user', {
                   body: {
                       full_name: userFormData.name,
                       email: userFormData.email,
                       password: userFormData.password,
                       role: formatRole
                   }
               });

               if (error) throw error;
               alert(`${userFormData.name} has been successfully added to the CRM!`);

               // Inject them into the view instantly
               const newUser = {
                   id: Date.now().toString(), // Temp ID until the next hard refresh
                   name: userFormData.name,
                   email: userFormData.email,
                   role: formatRole.replace('_', ' '),
                   status: 'Active',
                   lastLogin: 'Never',
                   twoFactor: false
               };
               setUsers([...users, newUser]);

           } else {
               // Update existing user
               const { error } = await supabase
                   .from('profiles')
                   .update({ 
                       full_name: userFormData.name,
                       role: formatRole
                   })
                   .eq('id', userModal.data.id);

               if (error) throw error;
               setUsers(users.map(u => u.id === userModal.data.id ? { ...u, name: userFormData.name, role: formatRole.replace('_', ' ') } : u));
           }
           
           // Clean up modal
           setUserModal({ show: false, mode: 'add', data: null });
           setUserFormData({ name: '', email: '', password: '', role: 'Sales', status: 'Active' });
           
       } catch (err) {
           console.error("Error saving user:", err);
           alert("Failed to save user: " + err.message);
       } finally {
           setIsCreating(false);
       }
   };

   // --- HANDLERS: PERMISSIONS ---
   const togglePermissionRole = (permId, role) => {
       setPermissions(permissions.map(p => {
           if (p.id === permId) {
               const hasRole = p.roles.includes(role);
               return {
                   ...p,
                   roles: hasRole ? p.roles.filter(r => r !== role) : [...p.roles, role]
               };
           }
           return p;
       }));
   };

   // THE FIX: Push the matrix to the global settings DB
   const handleSaveSchema = async () => {
       try {
           const { error } = await supabase
               .from('crm_settings')
               .update({ role_permissions: permissions })
               .eq('id', 'global');

           if (error) throw error;
           alert("Page permissions saved! The sidebar navigation will now update for all users.");
           setShowSchemaModal(false);
       } catch (err) {
           console.error("Error saving schema:", err);
           alert("Failed to save schema to database.");
       }
   };

   // --- HANDLERS: AUTOMATION (LIVE DB) ---
   const handleAddRule = async () => {
       if (!newRule.templateId) return alert("Please select a template");
       
       try {
           const { data, error } = await supabase
               .from('crm_automation_rules')
               .insert([{
                   trigger_event: newRule.trigger,
                   channel: newRule.channel,
                   template_id: newRule.templateId,
                   is_active: true
               }])
               .select();

           if (error) throw error;

           if (data && data[0]) {
               const savedRule = {
                   id: data[0].id,
                   trigger: data[0].trigger_event,
                   channel: data[0].channel,
                   templateId: data[0].template_id,
                   active: data[0].is_active
               };
               setAutomationRules([savedRule, ...automationRules]);
               setShowRuleModal(false);
           }
       } catch (err) {
           console.error("Error saving rule:", err);
           alert("Failed to save automation rule.");
       }
   };

   const deleteRule = async (id) => {
       if(!window.confirm("Are you sure you want to delete this automation rule?")) return;
       try {
           const { error } = await supabase.from('crm_automation_rules').delete().eq('id', id);
           if (error) throw error;
           setAutomationRules(automationRules.filter(r => r.id !== id));
       } catch (err) {
           console.error("Error deleting rule:", err);
       }
   };

   const toggleRule = async (id, currentStatus) => {
       try {
           const { error } = await supabase.from('crm_automation_rules').update({ is_active: !currentStatus }).eq('id', id);
           if (error) throw error;
           setAutomationRules(automationRules.map(r => r.id === id ? { ...r, active: !currentStatus } : r));
       } catch (err) {
           console.error("Error toggling rule:", err);
       }
   };

// --- UPDATED HANDLER: OPEN TEMPLATE MODAL ---
   const openTemplateModal = (mode, type, data = null) => {
       const initialData = data 
           ? { ...data, useCustomHtml: data.useCustomHtml || false, isSystem: data.isSystem || false, folder: data.folder || 'Uncategorized' } 
           : { id: '', label: '', subject: '', body: '', useCustomHtml: false, isSystem: false, folder: 'Uncategorized' }; // 🚨 NEW: Added folder
       
       setTemplateModal({ show: true, mode, type, data: initialData });
   };

   const handleSaveTemplate = async () => {
       const { mode, type, data } = templateModal;
       
       // Require the ID field now!
       if (!data.id || !data.label || !data.body) return alert('Template ID, Label, and Content are required.');
       if (type === 'email' && !data.subject) return alert('Subject is required for Email templates.');

       // Force ID to be lowercase with underscores (e.g., 'Welcome Email' -> 'welcome_email')
       const templateId = data.id.toLowerCase().replace(/\s+/g, '_');

       try {
           // Push to Supabase (Upsert updates if it exists, inserts if it doesn't)
           const { error } = await supabase
               .from('crm_templates')
               .upsert({ 
                   id: templateId,
                   type: type,
                   label: data.label,
                   subject: data.subject || '',
                   body: data.body,
                   use_custom_html: data.useCustomHtml || false,
                   is_system: data.isSystem || false,
                   folder: data.folder || 'Uncategorized', // 🚨 NEW: Save the folder
                   updated_at: new Date().toISOString()
               });

           if (error) throw error;

           // Update UI instantly
           const newTmpl = { ...data, id: templateId };
           if (mode === 'add') {
               if (type === 'email') setEmailTemplates([...emailTemplates, newTmpl]);
               else setWhatsappTemplates([...whatsappTemplates, newTmpl]);
           } else {
               if (type === 'email') setEmailTemplates(emailTemplates.map(t => t.id === data.id ? newTmpl : t));
               else setWhatsappTemplates(whatsappTemplates.map(t => t.id === data.id ? newTmpl : t));
           }
           setTemplateModal({ ...templateModal, show: false });
           
       } catch (err) {
           console.error("Error saving template:", err);
           alert("Failed to save template to database.");
       }
   };

   const handleDeleteTemplate = async (id, type) => {
       if(!window.confirm("Are you sure you want to delete this template?")) return;
       
       try {
           const { error } = await supabase.from('crm_templates').delete().eq('id', id);
           if (error) throw error;

           if (type === 'email') setEmailTemplates(emailTemplates.filter(t => t.id !== id));
           else setWhatsappTemplates(whatsappTemplates.filter(t => t.id !== id));
       } catch (err) {
           console.error("Error deleting template:", err);
           alert("Failed to delete template from database.");
       }
   };

   const handleSaveMaster = async () => {
       try {
           const { error } = await supabase
               .from('crm_templates')
               .update({ body: masterHtml, updated_at: new Date().toISOString() })
               .eq('id', 'master_html');

           if (error) throw error;
           alert("Master HTML Template successfully saved and locked into the database.");
       } catch (err) {
           console.error("Error saving master HTML:", err);
           alert("Failed to save Master HTML.");
       }
   };

   // --- HANDLERS: CONFIG & SECURITY ---
   const handleSaveSettings = async () => {
       setIsSavingConfig(true);
       try {
           const { error } = await supabase
               .from('crm_settings')
               .update({
                   maintenance_mode: sysConfig.maintenanceMode,
                   new_registrations: sysConfig.newRegistrations,
                   is_out_of_hours: sysConfig.outOfHours, // <--- ADDED HERE
                   enforce_2fa: securityConfig.enforce2FA,
                   ip_whitelist: securityConfig.ipWhitelist,
                   session_timeout: securityConfig.sessionTimeout
               })
               .eq('id', 'global');

           if (error) throw error;
           alert("Global system settings updated successfully.");
       } catch (err) {
           console.error("Error saving settings:", err);
           alert("Failed to save settings.");
       } finally {
           setIsSavingConfig(false);
       }
   };

   return (
      <div className="p-4 md:p-8 animate-fade-in relative">
         
         {/* MODAL: USER MANAGEMENT */}
         {userModal.show && (
             <div className="fixed inset-0 bg-slate-950/80 z-[100] flex items-center justify-center p-4">
                 <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6 animate-fade-in">
                     <div className="flex justify-between items-center mb-6">
                         <h3 className="text-xl font-bold text-white">{userModal.mode === 'add' ? 'Add New User' : 'Edit User'}</h3>
                         <button onClick={() => setUserModal({...userModal, show: false})} className="text-slate-400 hover:text-white"><X size={20}/></button>
                     </div>
                     <div className="space-y-4">
                         <div>
                             <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Full Name</label>
                             <input className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none" value={userFormData.name} onChange={(e) => setUserFormData({...userFormData, name: e.target.value})} />
                         </div>
                         <div>
                             <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Email Address</label>
                             <input className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none" value={userFormData.email} onChange={(e) => setUserFormData({...userFormData, email: e.target.value})} />
                         </div>
                         
                         {userModal.mode === 'add' && (
                             <div>
                                 <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Temporary Password</label>
                                 <input 
                                     type="password" 
                                     className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none" 
                                     value={userFormData.password} 
                                     placeholder="••••••••"
                                     onChange={(e) => setUserFormData({...userFormData, password: e.target.value})} 
                                 />
                             </div>
                         )}
                         <div className="grid grid-cols-2 gap-4">
                             <div>
                                <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Role</label>
                                <select 
                                    className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none" 
                                    value={userFormData.role} 
                                    onChange={(e) => setUserFormData({...userFormData, role: e.target.value})}
                                >
                                    {Object.entries(ROLE_CONFIG).map(([roleKey, roleData]) => (
                                        <option key={roleKey} value={roleKey}>
                                            {roleData.label} ({roleKey})
                                        </option>
                                    ))}
                                </select>
                            </div>
                             <div>
                                 <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Status</label>
                                 <select className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none" value={userFormData.status} onChange={(e) => setUserFormData({...userFormData, status: e.target.value})}>
                                     <option value="Active">Active</option>
                                     <option value="Suspended">Suspended</option>
                                 </select>
                             </div>
                         </div>
                         <button onClick={handleSaveUser} disabled={isCreating} className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-lg mt-4 flex items-center justify-center gap-2 transition-colors">
                             <Save size={18} /> {isCreating ? 'Saving...' : 'Save User'}
                         </button>
                     </div>
                 </div>
             </div>
         )}

         {/* MODAL: SCHEMA MODIFIER */}
         {showSchemaModal && (
             <div className="fixed inset-0 bg-slate-950/80 z-[100] flex items-center justify-center p-4">
                 <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl shadow-2xl p-6 animate-fade-in flex flex-col max-h-[80vh]">
                     <div className="flex justify-between items-center mb-6 border-b border-slate-800 pb-4">
                         <div>
                            <h3 className="text-xl font-bold text-white">Modify Permission Schema</h3>
                            <p className="text-slate-400 text-sm">Toggle roles to grant or revoke access to specific modules.</p>
                         </div>
                         <button onClick={() => setShowSchemaModal(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                     </div>
                     <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                         {permissions.map(p => (
                             <div key={p.id} className="bg-slate-950 p-4 rounded-xl border border-slate-800 flex justify-between items-center">
                                 <span className="text-sm font-bold text-white w-1/3">{p.label}</span>
                                 <div className="flex flex-wrap gap-2 flex-1 justify-end">
                                     {ROLES.map(role => {
                                         const hasPerm = p.roles.includes(role);
                                         return (
                                             <button 
                                                key={role}
                                                onClick={() => togglePermissionRole(p.id, role)}
                                                className={`px-3 py-1 rounded text-xs font-bold border transition-all ${
                                                    hasPerm 
                                                    ? 'bg-amber-500/20 text-amber-500 border-amber-500/50 hover:bg-amber-500/30' 
                                                    : 'bg-slate-900 text-slate-500 border-slate-700 hover:border-slate-500'
                                                }`}
                                             >
                                                 {role}
                                             </button>
                                         );
                                     })}
                                 </div>
                             </div>
                         ))}
                     </div>
                     <div className="pt-6 mt-2 border-t border-slate-800 flex justify-end gap-3 shrink-0">
                         <button onClick={() => setShowSchemaModal(false)} className="px-6 py-2 text-slate-400 hover:text-white font-bold transition-colors">Cancel</button>
                         <button onClick={handleSaveSchema} className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors shadow-lg shadow-blue-900/20">
                             <Save size={16} /> Save Schema
                         </button>
                     </div>
                 </div>
             </div>
         )}

         {/* MODAL: AUTOMATION RULE */}
         {showRuleModal && (
             <div className="fixed inset-0 bg-slate-950/80 z-[100] flex items-center justify-center p-4">
                 <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6 animate-fade-in">
                     <div className="flex justify-between items-center mb-6">
                         <h3 className="text-xl font-bold text-white">Create Automation Rule</h3>
                         <button onClick={() => setShowRuleModal(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                     </div>
                     <div className="space-y-4">
                         <div>
                             <label className="text-xs font-bold text-slate-400 uppercase block mb-2">1. System Trigger (IF)</label>
                             <select 
                                 className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-amber-500 outline-none"
                                 value={newRule.trigger}
                                 onChange={(e) => setNewRule({...newRule, trigger: e.target.value})}
                             >
                                 {SYSTEM_TRIGGERS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                             </select>
                         </div>
                         <div>
                             <label className="text-xs font-bold text-slate-400 uppercase block mb-2">2. Communication Channel (THEN)</label>
                             <div className="grid grid-cols-2 gap-3">
                                 <button 
                                     onClick={() => setNewRule({...newRule, channel: 'email'})}
                                     className={`p-3 rounded-lg border text-sm font-bold flex items-center justify-center gap-2 ${newRule.channel === 'email' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                                 >
                                     <Mail size={16}/> Email
                                 </button>
                                 <button 
                                     onClick={() => setNewRule({...newRule, channel: 'whatsapp'})}
                                     className={`p-3 rounded-lg border text-sm font-bold flex items-center justify-center gap-2 ${newRule.channel === 'whatsapp' ? 'bg-green-600 border-green-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                                 >
                                     <MessageSquare size={16}/> WhatsApp
                                 </button>
                             </div>
                         </div>
                         <div>
                             <label className="text-xs font-bold text-slate-400 uppercase block mb-2">3. Select Template</label>
                             <select 
                                 className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-amber-500 outline-none"
                                 value={newRule.templateId}
                                 onChange={(e) => setNewRule({...newRule, templateId: e.target.value})}
                             >
                                 <option value="">Select a template...</option>
                                 {(newRule.channel === 'email' ? emailTemplates : whatsappTemplates).map(t => (
                                     <option key={t.id} value={t.id}>{t.label}</option>
                                 ))}
                             </select>
                         </div>
                         <button 
                             onClick={handleAddRule}
                             className="w-full py-3 bg-amber-500 text-slate-900 font-bold rounded-lg hover:bg-amber-400 transition-colors mt-4 flex items-center justify-center gap-2"
                         >
                             <Zap size={18} /> Activate Rule
                         </button>
                     </div>
                 </div>
             </div>
         )}

         {/* MODAL: TEMPLATE EDITOR (WITH LIVE PREVIEW) */}
         {templateModal.show && (
             <div className="fixed inset-0 bg-slate-950/80 z-[100] flex items-center justify-center p-4">
                 {/* 🚨 THE EXPANDING PREVIEW FIX */}
                 <div className={`bg-slate-900 border border-slate-700 rounded-2xl w-full ${showPreview ? 'max-w-6xl h-[90vh]' : 'max-w-2xl max-h-[90vh]'} shadow-2xl p-6 animate-fade-in flex flex-col transition-all duration-300`}>
                     
                     <div className="flex justify-between items-center mb-6 shrink-0">
                         <div>
                             <h3 className="text-xl font-bold text-white">
                                 {templateModal.mode === 'add' ? 'New' : 'Edit'} {templateModal.type === 'email' ? 'Email' : 'WhatsApp'} Template
                             </h3>
                         </div>
                         <div className="flex bg-slate-950 p-1 rounded-lg border border-slate-800">
                             <button 
                                 onClick={() => setShowPreview(false)}
                                 className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${!showPreview ? 'bg-slate-800 text-white shadow' : 'text-slate-500 hover:text-slate-300'}`}
                             >
                                 Write
                             </button>
                             <button 
                                 onClick={() => setShowPreview(true)}
                                 className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${showPreview ? 'bg-blue-600 text-white shadow' : 'text-slate-500 hover:text-slate-300'}`}
                             >
                                 Preview
                             </button>
                         </div>
                         <button onClick={() => { setTemplateModal({...templateModal, show: false}); setShowPreview(false); }} className="text-slate-400 hover:text-white ml-4"><X size={20}/></button>
                     </div>

                     <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar min-h-0">
                         {!showPreview ? (
                                 <div className="space-y-4">
                                     <div>
                                         <label className="text-xs font-bold text-slate-400 uppercase block mb-2">System Template ID</label>
                                         <input 
                                             className={`w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm ${templateModal.mode === 'edit' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                             placeholder="e.g. welcome (No spaces)"
                                             value={templateModal.data.id || ''}
                                             onChange={(e) => setTemplateModal({...templateModal, data: {...templateModal.data, id: e.target.value}})}
                                             disabled={templateModal.mode === 'edit'} 
                                         />
                                         <p className="text-[10px] text-slate-500 mt-1">This is the exact code the backend Edge Function uses to find this email.</p>
                                     </div>

                                     <div>
                                         <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Internal Label Name</label>
                                         <input 
                                             className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                             placeholder="e.g. KYC Reminder Step 2"
                                             value={templateModal.data.label}
                                             onChange={(e) => setTemplateModal({...templateModal, data: {...templateModal.data, label: e.target.value}})}
                                         />
                                     </div>

                                     <div>
                                         <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Folder Category</label>
                                         <input 
                                             className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                             placeholder="e.g. KYC Emails, Marketing, Support"
                                             value={templateModal.data.folder || ''}
                                             onChange={(e) => setTemplateModal({...templateModal, data: {...templateModal.data, folder: e.target.value}})}
                                         />
                                     </div>
                                 
                                 {templateModal.type === 'email' && (
                                     <>
                                         <div>
                                             <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Email Subject Line</label>
                                             <input 
                                                 className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                                 placeholder="e.g. Action Required: Please verify your ID"
                                                 value={templateModal.data.subject}
                                                 onChange={(e) => setTemplateModal({...templateModal, data: {...templateModal.data, subject: e.target.value}})}
                                             />
                                         </div>

                                         <div className="flex items-center justify-between bg-slate-950 p-3 rounded-lg border border-slate-800">
                                             <div>
                                                 <p className="text-sm font-bold text-white">Use Custom HTML Design</p>
                                                 <p className="text-xs text-slate-500">Override Master Branding with custom code.</p>
                                             </div>
                                             <button 
                                                 onClick={() => setTemplateModal({
                                                     ...templateModal, 
                                                     data: {...templateModal.data, useCustomHtml: !templateModal.data.useCustomHtml}
                                                 })} 
                                                 className={`text-2xl transition-colors ${templateModal.data.useCustomHtml ? 'text-green-500' : 'text-slate-600'}`}
                                             >
                                                 {templateModal.data.useCustomHtml ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                                             </button>
                                         </div>
                                     </>
                                 )}

                                 <div className="flex items-center justify-between bg-slate-950 p-3 rounded-lg border border-slate-800">
                                     <div>
                                         <p className="text-sm font-bold text-white flex items-center gap-2"><Zap size={14} className="text-amber-500"/> System Automation Template</p>
                                         <p className="text-xs text-slate-500">Hide from agents. Only triggered by backend automations.</p>
                                     </div>
                                     <button 
                                         onClick={() => setTemplateModal({
                                             ...templateModal, 
                                             data: {...templateModal.data, isSystem: !templateModal.data.isSystem}
                                         })} 
                                         className={`text-2xl transition-colors ${templateModal.data.isSystem ? 'text-amber-500' : 'text-slate-600'}`}
                                     >
                                         {templateModal.data.isSystem ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                                     </button>
                                 </div>
                                 
                                 <div className="flex-1 flex flex-col min-h-[300px]">
                                     {!templateModal.data.useCustomHtml && (
                                         <div className="bg-slate-950 p-3 rounded-lg border border-slate-800 mb-4 animate-fade-in">
                                             <p className="text-[10px] font-bold text-slate-500 uppercase mb-2 flex items-center gap-1"><FileCode size={12}/> Smart Variables (Click to copy)</p>
                                             <div className="flex flex-wrap gap-1.5">
                                                 {['{{client_first_name}}', '{{client_last_name}}', '{{client_email}}', '{{account_id}}', '{{membership_tier}}', '{{account_balance}}', '{{ticket_id}}', '{{agent_name}}'].map(tag => (
                                                     <button 
                                                         key={tag} 
                                                         onClick={(e) => { e.preventDefault(); navigator.clipboard.writeText(tag); }} 
                                                         className="text-[10px] font-mono bg-slate-800 text-blue-400 px-2 py-1 rounded border border-slate-700 hover:border-blue-500 hover:bg-slate-700 transition-colors"
                                                         title={`Copy ${tag}`}
                                                     >
                                                         {tag}
                                                     </button>
                                                 ))}
                                             </div>
                                         </div>
                                     )}

                                     <label className="text-xs font-bold text-slate-400 uppercase block mb-2">
                                         {templateModal.data.useCustomHtml ? 'Full Custom HTML Code' : 'Message Body'}
                                     </label>
                                     <textarea
                                         className={`flex-1 w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none min-h-[250px] font-mono text-sm ${templateModal.data.useCustomHtml ? 'text-green-400' : 'text-slate-300'}`}
                                         placeholder={templateModal.data.useCustomHtml ? "<html><body>...</body></html>" : "Type your plain text content here..."}
                                         value={templateModal.data.body}
                                         onChange={(e) => setTemplateModal({...templateModal, data: {...templateModal.data, body: e.target.value}})}
                                     ></textarea>
                                 </div>
                             </div>
                         ) : (
                             <div className="h-full flex flex-col min-h-[400px]">
                                 <div className="bg-slate-950 border border-slate-800 rounded-t-lg p-2 flex items-center gap-2 text-xs text-slate-500">
                                     <div className="w-3 h-3 rounded-full bg-red-500/20"></div>
                                     <div className="w-3 h-3 rounded-full bg-amber-500/20"></div>
                                     <div className="w-3 h-3 rounded-full bg-green-500/20"></div>
                                     <span className="ml-2 font-mono">Simulated Email Client Preview</span>
                                 </div>
                                 <div className="flex-1 bg-white rounded-b-lg overflow-hidden border border-slate-800 border-t-0">
                                     <iframe 
                                         title="email-preview"
                                         className="w-full h-full"
                                         srcDoc={
                                             templateModal.data.useCustomHtml 
                                                 ? templateModal.data.body 
                                                 : masterHtml.replace('{{CONTENT_BODY}}', templateModal.data.body.replace(/\n/g, '<br/>'))
                                         }
                                     />
                                 </div>
                             </div>
                         )}
                     </div>
                     
                     {!showPreview && (
                         <div className="pt-4 border-t border-slate-800 flex justify-end gap-3 mt-4 shrink-0">
                             <button onClick={() => { setTemplateModal({...templateModal, show: false}); setShowPreview(false); }} className="px-4 py-2 text-slate-400 hover:text-white font-bold">Cancel</button>
                             <button onClick={handleSaveTemplate} className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2">
                                 <Save size={16} /> Save Template
                             </button>
                         </div>
                     )}
                 </div>
             </div>
         )}

         {/* MAIN HEADER & TABS */}
         <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
             <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">System Administration</h2>
                <p className="text-slate-400 mt-1">Platform configuration and staff management.</p>
             </div>
             
             <div className="bg-slate-900 p-1 rounded-lg border border-slate-800 flex overflow-x-auto w-full md:w-auto custom-scrollbar">
                 {['users', 'config', 'security', 'automation', 'templates'].map(tab => (
                     <button 
                        key={tab}
                        onClick={() => setActiveTab(tab)} 
                        className={`px-4 py-2 rounded-md text-sm font-bold whitespace-nowrap transition-all flex items-center gap-2 capitalize ${activeTab === tab ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                     >
                        {tab === 'users' && <Users size={14} />}
                        {tab === 'config' && <Settings size={14} />}
                        {tab === 'security' && <Shield size={14} />}
                        {tab === 'automation' && <Zap size={14} />}
                        {tab === 'templates' && <LayoutTemplate size={14} />}
                        {tab}
                     </button>
                 ))}
             </div>
         </div>

         {/* --- AUTOMATION TAB --- */}
         {activeTab === 'automation' && (
             <div className="space-y-6 animate-fade-in">
                <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h3 className="font-bold text-white flex items-center gap-2 text-lg"><Zap size={20} className="text-amber-500" /> Automation Rules Engine</h3>
                            <p className="text-slate-400 text-sm mt-1">Define "If This, Then That" logic for client communications.</p>
                        </div>
                        <button 
                            onClick={() => setShowRuleModal(true)} 
                            className="bg-amber-500 hover:bg-amber-400 text-slate-900 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors"
                        >
                            <Plus size={16} /> New Rule
                        </button>
                    </div>

                    <div className="space-y-3">
                        {automationRules.map((rule) => {
                            const template = (rule.channel === 'email' ? emailTemplates : whatsappTemplates).find(t => t.id === rule.templateId);
                            const triggerLabel = SYSTEM_TRIGGERS.find(t => t.id === rule.trigger)?.label || rule.trigger;
                            
                            return (
                                <div key={rule.id} className="bg-slate-950 p-4 rounded-xl border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4 group">
                                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                                        <div className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5">
                                            <span className="text-[10px] font-bold text-slate-500">IF</span>
                                            <span className="text-sm font-bold text-white">{triggerLabel}</span>
                                        </div>
                                        <div className="w-8 h-px bg-slate-700 hidden md:block"></div>
                                        <div className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5">
                                            <span className="text-[10px] font-bold text-slate-500">THEN</span>
                                            {rule.channel === 'email' ? <Mail size={14} className="text-blue-400"/> : <MessageSquare size={14} className="text-green-400"/>}
                                            <span className="text-sm font-bold text-white">{template?.label || 'Unknown Template'}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button onClick={() => toggleRule(rule.id, rule.active)} className="flex items-center gap-2 hover:bg-slate-800 px-2 py-1 rounded transition-colors" title="Click to pause/resume rule">
                                            <span className="text-xs text-slate-500">{rule.active ? 'Active' : 'Paused'}</span>
                                            <div className={`w-2 h-2 rounded-full ${rule.active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                        </button>
                                        <button onClick={() => deleteRule(rule.id)} className="p-2 text-slate-600 hover:text-red-500 transition-colors">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {automationRules.length === 0 && (
                            <div className="text-center py-12 text-slate-500 border border-dashed border-slate-800 rounded-xl">No active automation rules.</div>
                        )}
                    </div>
                </div>
             </div>
         )}

         {/* --- TEMPLATES TAB --- */}
         {activeTab === 'templates' && (
             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in">
                 
                 {/* Email Templates List */}
                 <div className="lg:col-span-1 space-y-6">
                     <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 flex flex-col max-h-[600px]">
                         <div className="flex justify-between items-center mb-4 shrink-0">
                             <h3 className="font-bold text-white flex items-center gap-2"><Mail size={18} className="text-blue-500"/> Email Templates</h3>
                             <button 
                                onClick={() => openTemplateModal('add', 'email')}
                                className="text-sm font-bold bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
                             >
                                 <Plus size={16}/> New
                             </button>
                         </div>

                         {/* 🚨 THE FOLDERS FIX: Mathmatically locked flex items so they CANNOT stretch into circles */}
                         <div className="flex flex-wrap items-start gap-2 pb-4 mb-4 border-b border-slate-800 shrink-0">
                             {['All', ...new Set(emailTemplates.map(t => t.folder || 'Uncategorized'))].map(folder => (
                                 <button
                                     key={folder}
                                     onClick={() => setActiveFolder(folder)}
                                     className={`h-8 px-4 flex-none inline-flex items-center justify-center rounded-lg text-xs font-bold whitespace-nowrap transition-colors border ${activeFolder === folder ? 'bg-blue-600/20 text-blue-400 border-blue-500/30' : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700 hover:text-white'}`}
                                 >
                                     {folder}
                                 </button>
                             ))}
                         </div>

                         {/* 🚨 THE BUTTONS FIX: Cards locked to flex-1 overflow scrolling so they never stretch */}
                         <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 pr-2">
                             {emailTemplates.filter(t => activeFolder === 'All' || (t.folder || 'Uncategorized') === activeFolder).map(t => (
                                 <div key={t.id} className="p-4 bg-slate-950 rounded-xl border border-slate-800 flex flex-col gap-3 group hover:border-slate-600 transition-colors h-auto shrink-0">
                                     <div>
                                         <div className="flex items-center gap-3 mb-1">
                                             <span className="text-base text-white font-bold truncate">{t.label}</span>
                                             {t.isSystem ? (
                                                 <span className="text-[10px] bg-amber-500/10 text-amber-500 border border-amber-500/20 px-2 py-0.5 rounded uppercase font-bold tracking-wider shrink-0">System</span>
                                             ) : (
                                                 <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded uppercase font-bold tracking-wider shrink-0">Ad-Hoc</span>
                                             )}
                                         </div>
                                         <p className="text-sm text-slate-500 truncate max-w-full">{t.subject}</p>
                                     </div>
                                     <div className="flex gap-2 mt-2">
                                         <button 
                                            onClick={(e) => { e.stopPropagation(); openTemplateModal('edit', 'email', t); }} 
                                            className="flex-1 flex items-center justify-center gap-2 py-2 bg-slate-800 hover:bg-blue-600 text-slate-300 hover:text-white rounded-lg transition-colors border border-slate-700 hover:border-blue-500 text-xs font-bold"
                                         >
                                             <Edit3 size={14} /> Edit
                                         </button>
                                         <button 
                                            onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t.id, 'email'); }} 
                                            className="px-4 py-2 bg-slate-800 hover:bg-red-600 text-slate-400 hover:text-white rounded-lg transition-colors border border-slate-700 hover:border-red-500"
                                         >
                                             <Trash2 size={16} />
                                         </button>
                                     </div>
                                 </div>
                             ))}
                             {emailTemplates.filter(t => activeFolder === 'All' || (t.folder || 'Uncategorized') === activeFolder).length === 0 && (
                                 <div className="text-center py-8 text-slate-500 border border-dashed border-slate-800 rounded-xl">No templates in this folder.</div>
                             )}
                         </div>
                     </div>

                     {/* WhatsApp Templates List */}
                     <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 flex flex-col max-h-[600px]">
                         <div className="flex justify-between items-center mb-4 shrink-0">
                             <h3 className="font-bold text-white flex items-center gap-2"><MessageSquare size={18} className="text-green-500"/> WhatsApp Templates</h3>
                             <button 
                                onClick={() => openTemplateModal('add', 'whatsapp')}
                                className="text-sm font-bold bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
                             >
                                 <Plus size={16}/> New
                             </button>
                         </div>
                         <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 pr-2">
                             {whatsappTemplates.map(t => (
                                 <div key={t.id} className="p-4 bg-slate-950 rounded-xl border border-slate-800 flex flex-col gap-3 group hover:border-slate-600 transition-colors h-auto shrink-0">
                                     <div>
                                         <div className="flex items-center gap-3 mb-1">
                                             <span className="text-base text-white font-bold truncate">{t.label}</span>
                                             {t.isSystem ? (
                                                 <span className="text-[10px] bg-amber-500/10 text-amber-500 border border-amber-500/20 px-2 py-0.5 rounded uppercase font-bold tracking-wider shrink-0">System</span>
                                             ) : (
                                                 <span className="text-[10px] bg-green-500/10 text-green-400 border border-green-500/20 px-2 py-0.5 rounded uppercase font-bold tracking-wider shrink-0">Ad-Hoc</span>
                                             )}
                                         </div>
                                     </div>
                                     <div className="flex gap-2 mt-2">
                                         <button 
                                            onClick={(e) => { e.stopPropagation(); openTemplateModal('edit', 'whatsapp', t); }} 
                                            className="flex-1 flex items-center justify-center gap-2 py-2 bg-slate-800 hover:bg-green-600 text-slate-300 hover:text-white rounded-lg transition-colors border border-slate-700 hover:border-green-500 text-xs font-bold"
                                         >
                                             <Edit3 size={14} /> Edit
                                         </button>
                                         <button 
                                            onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t.id, 'whatsapp'); }} 
                                            className="px-4 py-2 bg-slate-800 hover:bg-red-600 text-slate-400 hover:text-white rounded-lg transition-colors border border-slate-700 hover:border-red-500"
                                         >
                                             <Trash2 size={16} />
                                         </button>
                                     </div>
                                 </div>
                             ))}
                             {whatsappTemplates.length === 0 && (
                                 <div className="text-center py-8 text-slate-500 border border-dashed border-slate-800 rounded-xl">No templates yet.</div>
                             )}
                         </div>
                     </div>
                 </div>

                 {/* Master HTML Editor */}
                 <div className="lg:col-span-2 bg-slate-900 rounded-2xl border border-slate-800 flex flex-col h-[600px] lg:sticky lg:top-24">
                     <div className="p-4 border-b border-slate-800 flex justify-between items-center shrink-0">
                         <div>
                             <h3 className="font-bold text-white flex items-center gap-2"><FileCode size={18} className="text-purple-500"/> Master HTML Branding</h3>
                             <p className="text-xs text-slate-400 mt-1">This HTML wrapper is applied to ALL outgoing system emails.</p>
                         </div>
                         <button 
                            onClick={handleSaveMaster}
                            className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors"
                         >
                             <Save size={16} /> Save Master
                         </button>
                     </div>
                     <div className="flex-1 p-0 relative min-h-0">
                         <textarea 
                             className="absolute inset-0 w-full h-full bg-[#0d1117] text-slate-300 font-mono text-sm p-4 resize-none focus:outline-none custom-scrollbar"
                             value={masterHtml}
                             onChange={(e) => setMasterHtml(e.target.value)}
                         ></textarea>
                     </div>
                 </div>
             </div>
         )}

         {/* --- USERS TAB --- */}
         {activeTab === 'users' && (
             <div className="space-y-6 animate-fade-in">
                 <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                     <div className="p-4 border-b border-slate-800 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                         <div className="relative w-full sm:w-auto">
                             <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                             <input className="bg-slate-950 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 outline-none w-full sm:w-64" placeholder="Search staff..." />
                         </div>
                         <button 
                            onClick={() => handleOpenUserModal('add')}
                            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 shrink-0"
                         >
                             <Plus size={16} /> Add User
                         </button>
                     </div>
                     <div className="overflow-x-auto">
                         <table className="w-full text-left min-w-[800px]">
                             <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase">
                                 <tr>
                                     <th className="px-6 py-4">Name</th>
                                     <th className="px-6 py-4">Role</th>
                                     <th className="px-6 py-4">Status</th>
                                     <th className="px-6 py-4">Security</th>
                                     <th className="px-6 py-4 text-right">Last Login</th>
                                     <th className="px-6 py-4 text-right">Action</th>
                                 </tr>
                             </thead>
                             <tbody className="divide-y divide-slate-800 text-sm">
                                 {users.map(user => (
                                     <tr key={user.id} className="hover:bg-slate-800/50 transition-colors">
                                         <td className="px-6 py-4">
                                             <p className="font-bold text-white">{user.name}</p>
                                             <p className="text-xs text-slate-500">{user.email}</p>
                                         </td>
                                         <td className="px-6 py-4">
                                             <span className="bg-slate-800 text-slate-300 px-2 py-1 rounded text-xs font-bold border border-slate-700">{user.role}</span>
                                         </td>
                                         <td className="px-6 py-4">
                                             <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${user.status === 'Active' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                                                 {user.status}
                                             </span>
                                         </td>
                                         <td className="px-6 py-4">
                                             {user.twoFactor ? 
                                                 <span className="text-green-500 flex items-center gap-1 text-xs"><Lock size={12}/> 2FA On</span> : 
                                                 <span className="text-slate-500 flex items-center gap-1 text-xs"><Lock size={12}/> 2FA Off</span>
                                             }
                                         </td>
                                         <td className="px-6 py-4 text-right text-slate-400 font-mono text-xs">{user.lastLogin}</td>
                                         <td className="px-6 py-4">
                                             <div className="flex items-center justify-end gap-3">
                                                 <button onClick={() => handleOpenUserModal('edit', user)} className="text-blue-400 hover:text-blue-300 transition-colors" title="Edit Profile">
                                                     <Edit3 size={16} />
                                                 </button>
                                                 
                                                 {user.status === 'Suspended' ? (
                                                     <button onClick={() => handleUserAction(user.id, 'restore')} className="text-green-500 hover:text-green-400 transition-colors" title="Restore Access">
                                                         <Check size={16} />
                                                     </button>
                                                 ) : (
                                                     <button onClick={() => handleUserAction(user.id, 'suspend')} className="text-amber-500 hover:text-amber-400 transition-colors" title="Suspend Access">
                                                         <Ban size={16} />
                                                     </button>
                                                 )}

                                                 <button onClick={() => handleUserAction(user.id, 'archive')} className="text-slate-400 hover:text-slate-300 transition-colors" title="Archive User">
                                                     <Archive size={16} />
                                                 </button>
                                                 
                                                 <button onClick={() => handleUserAction(user.id, 'delete')} className="text-red-500 hover:text-red-400 transition-colors" title="Delete User">
                                                     <Trash2 size={16} />
                                                 </button>
                                             </div>
                                         </td>
                                     </tr>
                                 ))}
                             </tbody>
                         </table>
                     </div>
                 </div>

                 <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
                    <div className="flex justify-between mb-4">
                        <h3 className="font-bold text-white">Role Permission Matrix</h3>
                        <button 
                            onClick={() => setShowSchemaModal(true)}
                            className="text-xs bg-slate-800 px-3 py-1.5 rounded text-slate-300 border border-slate-700 hover:bg-slate-700 hover:text-white transition-colors font-bold"
                        >
                            Modify Schema
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                       {permissions.map(p => (
                          <div key={p.id} className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                             <p className="text-sm font-bold text-white mb-3">{p.label}</p>
                             <div className="flex flex-wrap gap-1.5">
                                 {p.roles.map(r => <span key={r} className="text-[10px] bg-slate-900 border border-slate-700 px-2 py-1 rounded font-bold text-slate-400">{r}</span>)}
                             </div>
                          </div>
                       ))}
                    </div>
                 </div>
             </div>
         )}

         {/* --- CONFIG TAB --- */}
         {activeTab === 'config' && (
             <div className="grid grid-cols-1 gap-8 animate-fade-in">
                 <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
                     <h3 className="font-bold text-white mb-6 flex items-center gap-2"><Server size={18} className="text-amber-500"/> System Status & Access</h3>
                     <div className="space-y-4">
                         
                         <div className="flex items-center justify-between p-4 bg-slate-950 rounded-xl border border-slate-700">
                             <div><p className="text-sm font-bold text-white">Maintenance Mode</p><p className="text-xs text-slate-500">Disables client login. Staff access only.</p></div>
                             <button onClick={() => setSysConfig({...sysConfig, maintenanceMode: !sysConfig.maintenanceMode})} className={`text-2xl ${sysConfig.maintenanceMode ? 'text-green-500' : 'text-slate-600'}`}>{sysConfig.maintenanceMode ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}</button>
                         </div>
                         
                         <div className="flex items-center justify-between p-4 bg-slate-950 rounded-xl border border-slate-700">
                             <div><p className="text-sm font-bold text-white">New Registrations</p><p className="text-xs text-slate-500">Allow new clients to sign up.</p></div>
                             <button onClick={() => setSysConfig({...sysConfig, newRegistrations: !sysConfig.newRegistrations})} className={`text-2xl ${sysConfig.newRegistrations ? 'text-green-500' : 'text-slate-600'}`}>{sysConfig.newRegistrations ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}</button>
                         </div>

                         <div className="flex items-center justify-between p-4 bg-slate-950 rounded-xl border border-slate-700">
                             <div>
                                 <p className="text-sm font-bold text-white flex items-center gap-2">
                                     <Moon size={14} className="text-purple-400" /> Out of Hours (OOH) Mode
                                 </p>
                                 <p className="text-xs text-slate-500">Enable chatbot OOH data collection forms.</p>
                             </div>
                             <button onClick={() => setSysConfig({...sysConfig, outOfHours: !sysConfig.outOfHours})} className={`text-2xl ${sysConfig.outOfHours ? 'text-purple-500' : 'text-slate-600'}`}>
                                 {sysConfig.outOfHours ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                             </button>
                         </div>

                     </div>
                     <div className="mt-6 pt-6 border-t border-slate-800 flex justify-end">
                         <button 
                             onClick={handleSaveSettings}
                             className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors"
                         >
                             <Save size={16} /> Save Changes
                         </button>
                     </div>
                 </div>
             </div>
         )}

         {/* --- SECURITY TAB --- */}
         {activeTab === 'security' && (
             <div className="grid grid-cols-1 gap-8 animate-fade-in">
                 <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
                     <h3 className="font-bold text-white mb-6 flex items-center gap-2"><Shield size={18} className="text-red-500"/> Staff Security Policy</h3>
                     <div className="space-y-6">
                         <div className="flex items-center justify-between"><span className="text-sm text-slate-300">Enforce 2FA for all roles</span><button onClick={() => setSecurityConfig({...securityConfig, enforce2FA: !securityConfig.enforce2FA})} className={`text-2xl transition-colors ${securityConfig.enforce2FA ? 'text-green-500' : 'text-slate-600'}`}>{securityConfig.enforce2FA ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}</button></div>
                         <div className="flex items-center justify-between"><span className="text-sm text-slate-300">Admin IP Whitelist Only</span><button onClick={() => setSecurityConfig({...securityConfig, ipWhitelist: !securityConfig.ipWhitelist})} className={`text-2xl transition-colors ${securityConfig.ipWhitelist ? 'text-green-500' : 'text-slate-600'}`}>{securityConfig.ipWhitelist ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}</button></div>
                         
                         <div>
                             <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Session Timeout (Minutes)</label>
                             <input type="range" min="1" max="120" value={securityConfig.sessionTimeout} onChange={(e) => setSecurityConfig({...securityConfig, sessionTimeout: parseInt(e.target.value)})} className="w-full h-2 bg-slate-950 rounded-lg appearance-none cursor-pointer accent-blue-500" />
                             <div className="text-right text-xs text-blue-400 font-bold mt-1">{securityConfig.sessionTimeout} mins</div>
                         </div>
                     </div>
                     
                     <div className="mt-6 pt-6 border-t border-slate-800 flex justify-end">
                         <button 
                             onClick={handleSaveSettings}
                             disabled={isSavingConfig}
                             className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors disabled:opacity-50"
                         >
                             <Save size={16} /> {isSavingConfig ? 'Saving...' : 'Save Security Settings'}
                         </button>
                     </div>
                 </div>
             </div>
         )}

      </div>
   );
};

export default AdminPage;